import * as THREE from 'three';
import { uiState } from './state/ui.svelte';
export function initializeSequence(host, files, wildcard) {
    host.sequenceMode = true;
    host.sequenceFiles = files;
    host.sequenceIndex = 0;
    host.sequenceTargetIndex = 0;
    host.sequenceDidInitialFit = false;
    host.isSequencePlaying = false;
    host.sequenceCache.clear();
    host.sequenceCacheOrder = [];
    uiState.sequenceMode = true;
    updateSequenceUI(host);
    // Clear any existing meshes from normal mode
    for (const obj of host.meshes) {
        host.scene.remove(obj);
    }
    host.meshes = [];
    host.spatialFiles = [];
    // Load first frame
    if (files.length > 0) {
        loadSequenceFrame(host, 0);
    }
    host.updateFileList();
}
export function updateSequenceUI(host) {
    uiState.sequenceTotal = host.sequenceFiles.length;
    uiState.sequenceIndex = Math.min(host.sequenceIndex, host.sequenceFiles.length ? host.sequenceFiles.length - 1 : 0);
}
export function playSequence(host) {
    if (!host.sequenceFiles.length) {
        return;
    }
    if (host.isSequencePlaying) {
        return;
    }
    host.isSequencePlaying = true;
    uiState.isSequencePlaying = true;
    const intervalMs = Math.max(50, Math.floor(1000 / host.sequenceFps));
    host.sequenceTimer = window.setInterval(() => {
        const nextIndex = (host.sequenceIndex + 1) % host.sequenceFiles.length;
        seekSequence(host, nextIndex);
    }, intervalMs);
}
export function pauseSequence(host) {
    host.isSequencePlaying = false;
    uiState.isSequencePlaying = false;
    if (host.sequenceTimer !== null) {
        window.clearInterval(host.sequenceTimer);
        host.sequenceTimer = null;
    }
}
export function stopSequence(host) {
    pauseSequence(host);
}
export function stepSequence(host, delta) {
    if (!host.sequenceFiles.length) {
        return;
    }
    pauseSequence(host); // do not auto-play when stepping
    const count = host.sequenceFiles.length;
    const next = (host.sequenceIndex + delta + count) % count;
    seekSequence(host, next);
}
export function seekSequence(host, index) {
    if (!host.sequenceFiles.length) {
        return;
    }
    const clamped = Math.max(0, Math.min(index, host.sequenceFiles.length - 1));
    host.sequenceTargetIndex = clamped;
    loadSequenceFrame(host, clamped);
}
export async function sequenceHandleUltimate(host, message) {
    const plyMsg = { ...message, type: 'ultimateRawBinaryData', messageType: 'addFiles' };
    const startFilesLen = host.spatialFiles.length;
    await host.handleUltimateRawBinaryData(plyMsg);
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        if (message.index === host.sequenceTargetIndex) {
            useSequenceObject(host, created, message.index);
        }
        else {
            cacheSequenceOnly(host, created, message.index);
        }
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export async function sequenceHandlePly(host, message) {
    const startFilesLen = host.spatialFiles.length;
    await host.displayFiles([message.data]);
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        if (message.index === host.sequenceTargetIndex) {
            useSequenceObject(host, created, message.index);
        }
        else {
            cacheSequenceOnly(host, created, message.index);
        }
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export async function sequenceHandleXyz(host, message) {
    const startFilesLen = host.spatialFiles.length;
    await host.handleXyzData({
        type: 'xyzData',
        fileName: message.fileName,
        data: message.data,
        isAddFile: true,
    });
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        if (message.index === host.sequenceTargetIndex) {
            useSequenceObject(host, created, message.index);
        }
        else {
            cacheSequenceOnly(host, created, message.index);
        }
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export async function sequenceHandleObj(host, message) {
    const startFilesLen = host.spatialFiles.length;
    await host.handleObjData({
        type: 'objData',
        fileName: message.fileName,
        data: message.data,
        isAddFile: true,
    });
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        if (message.index === host.sequenceTargetIndex) {
            useSequenceObject(host, created, message.index);
        }
        else {
            cacheSequenceOnly(host, created, message.index);
        }
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export async function sequenceHandleStl(host, message) {
    const startFilesLen = host.spatialFiles.length;
    await host.handleStlData({
        type: 'stlData',
        fileName: message.fileName,
        data: message.data,
        isAddFile: true,
    });
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        if (message.index === host.sequenceTargetIndex) {
            useSequenceObject(host, created, message.index);
        }
        else {
            cacheSequenceOnly(host, created, message.index);
        }
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export async function sequenceHandleDepth(host, message) {
    const startFilesLen = host.spatialFiles.length;
    await host.handleDepthData({
        type: 'depthData',
        fileName: message.fileName,
        data: message.data,
        isAddFile: true,
    });
    const created = host.meshes[host.meshes.length - 1];
    if (created) {
        useSequenceObject(host, created, message.index);
    }
    trimNormalModeArraysFrom(host, startFilesLen);
}
export function trimNormalModeArraysFrom(host, startIndex) {
    if (host.spatialFiles.length > startIndex) {
        host.spatialFiles.splice(startIndex);
    }
    if (host.multiMaterialGroups.length > startIndex) {
        host.multiMaterialGroups.splice(startIndex);
    }
    if (host.materialMeshes.length > startIndex) {
        host.materialMeshes.splice(startIndex);
    }
    if (host.fileVisibility.length > startIndex) {
        host.fileVisibility.splice(startIndex);
    }
    if (host.pointSizes.length > startIndex) {
        host.pointSizes.splice(startIndex);
    }
    if (host.individualColorModes.length > startIndex) {
        host.individualColorModes.splice(startIndex);
    }
}
export async function loadSequenceFrame(host, index) {
    const filePath = host.sequenceFiles[index];
    if (!filePath) {
        return;
    }
    // If cached, display immediately
    const cached = host.sequenceCache.get(index);
    if (cached) {
        swapSequenceObject(host, cached, index);
        return;
    }
    // If a request is in-flight and for a different index, let it finish but ignore on arrival
    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Request from extension with requestId for matching
    host.vscode.postMessage({ type: 'sequence:requestFile', path: filePath, index, requestId });
    // Show a lightweight loading hint
    try {
        document.getElementById('loading')?.classList.remove('hidden');
    }
    catch { }
}
export function useSequenceObject(host, obj, index) {
    // Cache management
    if (!host.sequenceCache.has(index)) {
        host.sequenceCache.set(index, obj);
        host.sequenceCacheOrder.push(index);
        // Evict if over capacity
        while (host.sequenceCacheOrder.length > host.maxSequenceCache) {
            const evictIndex = host.sequenceCacheOrder.shift();
            if (evictIndex !== host.sequenceIndex) {
                const evictObj = host.sequenceCache.get(evictIndex);
                if (evictObj) {
                    host.scene.remove(evictObj);
                    if (evictObj.geometry) {
                        evictObj.geometry.dispose?.();
                    }
                    if (evictObj.material) {
                        const mat = evictObj.material;
                        if (Array.isArray(mat)) {
                            mat.forEach(m => m.dispose?.());
                        }
                        else {
                            mat.dispose?.();
                        }
                    }
                }
                host.sequenceCache.delete(evictIndex);
            }
        }
    }
    swapSequenceObject(host, obj, index);
}
export function cacheSequenceOnly(host, obj, index) {
    if (obj.parent) {
        host.scene.remove(obj);
    }
    if (!host.sequenceCache.has(index)) {
        host.sequenceCache.set(index, obj);
        host.sequenceCacheOrder.push(index);
        while (host.sequenceCacheOrder.length > host.maxSequenceCache) {
            const evictIndex = host.sequenceCacheOrder.shift();
            const evictObj = host.sequenceCache.get(evictIndex);
            if (evictObj) {
                host.scene.remove(evictObj);
                if (evictObj.geometry) {
                    evictObj.geometry.dispose?.();
                }
                if (evictObj.material) {
                    const mat = evictObj.material;
                    if (Array.isArray(mat)) {
                        mat.forEach(m => m.dispose?.());
                    }
                    else {
                        mat.dispose?.();
                    }
                }
            }
            host.sequenceCache.delete(evictIndex);
        }
    }
}
export function swapSequenceObject(host, obj, index) {
    // Remove current
    const current = host.sequenceCache.get(host.sequenceIndex);
    if (current && current !== obj) {
        current.visible = false;
        host.scene.remove(current);
    }
    // Add new
    if (!obj.parent) {
        host.scene.add(obj);
    }
    obj.visible = true;
    // Hide axes when new object is added to rule out looking-only-at-axes confusion
    try {
        host.axesGroup.visible = true;
    }
    catch { }
    host.requestRender();
    host.sequenceIndex = index;
    // Make points clearly visible in sequence mode
    ensureSequenceVisibility(obj);
    // Fit camera only once on the first visible frame
    if (!host.sequenceDidInitialFit) {
        host.fitCameraToObject(obj);
        host.sequenceDidInitialFit = true;
    }
    updateSequenceUI(host);
    host.updateFileList();
    // Hide loading if it was shown
    try {
        document.getElementById('loading')?.classList.add('hidden');
    }
    catch { }
    // Preload next
    const next = (index + 1) % host.sequenceFiles.length;
    const nextPath = host.sequenceFiles[next] || '';
    const isDepth = /\.(tif|tiff|pfm|npy|npz|png|exr)$/i.test(nextPath);
    if (!isDepth && !host.sequenceCache.get(next)) {
        host.vscode.postMessage({ type: 'sequence:requestFile', path: nextPath, index: next });
    }
}
export function ensureSequenceVisibility(obj) {
    if (obj.isPoints &&
        obj.material &&
        obj.material instanceof THREE.PointsMaterial) {
        const mat = obj.material;
        // Use a sensible on-screen size for sequence mode; avoid tiny defaults
        if (!mat.size || mat.size < 0.5) {
            mat.size = 2.5;
        }
        // Use screen-space size for clarity regardless of distance
        mat.sizeAttenuation = false;
        mat.needsUpdate = true;
    }
}
